import { useState, type ReactNode } from 'react';
import { usePathname } from 'expo-router';

import { BudgetsTab } from '@/components/finance/BudgetsTab.web';
import { FinanceShell, type FinanceTab } from '@/components/finance/FinanceShell.web';
import { ImportWizard } from '@/components/finance/ImportWizard.web';
import { ImportHistory } from '@/components/finance/smart-import/ImportHistory.web';
import { ImportReview } from '@/components/finance/smart-import/ImportReview.web';
import { SmartImportDialog } from '@/components/finance/smart-import/SmartImportDialog.web';
import { RecordTransaction } from '@/components/finance/RecordTransaction.web';
import { ReportsTab } from '@/components/finance/reports/ReportsTab.web';
import { TransactionsLedger } from '@/components/finance/TransactionsLedger.web';
import { AccountsEditor } from '@/components/finance/master/AccountsEditor.web';
import { CategoriesEditor } from '@/components/finance/master/CategoriesEditor.web';
import { CurrenciesEditor } from '@/components/finance/master/CurrenciesEditor.web';
import { FxRatesEditor } from '@/components/finance/master/FxRatesEditor.web';
import {
  MasterDataShell,
  type MasterSection,
} from '@/components/finance/master/MasterDataShell.web';
import { useFinanceMonth } from '@/lib/finance/use-finance-month';

function masterSectionFromPath(path: string): MasterSection {
  if (path.endsWith('/currencies')) return 'currencies';
  if (path.endsWith('/fx')) return 'fx';
  if (path.endsWith('/categories')) return 'categories';
  return 'accounts';
}

function masterEditor(section: MasterSection) {
  switch (section) {
    case 'currencies':
      return <CurrenciesEditor />;
    case 'fx':
      return <FxRatesEditor />;
    case 'categories':
      return <CategoriesEditor />;
    case 'accounts':
    default:
      return <AccountsEditor />;
  }
}

export function FinanceScreen() {
  const path = usePathname();
  const [month, setMonth] = useFinanceMonth();
  const [smartImportOpen, setSmartImportOpen] = useState(false);

  if (path.startsWith('/finance/master')) {
    const section = masterSectionFromPath(path);
    return (
      <FinanceShell activeTab="master">
        <MasterDataShell active={section}>{masterEditor(section)}</MasterDataShell>
      </FinanceShell>
    );
  }

  let tab: FinanceTab = 'transactions';
  if (path.startsWith('/finance/budgets')) tab = 'budgets';
  else if (path.startsWith('/finance/reports')) tab = 'reports';

  const isLegacyImport = path === '/finance/transactions/import';
  const isSmartImportHistory = path === '/finance/transactions/smart-import';
  const isSmartImportReview = path.startsWith('/finance/transactions/smart-import/');
  const isRecord =
    !isLegacyImport &&
    !isSmartImportHistory &&
    !isSmartImportReview &&
    /^\/finance\/transactions\/.+/.test(path);

  let content: ReactNode;
  if (isLegacyImport) {
    content = <ImportWizard />;
  } else if (isSmartImportHistory) {
    content = <ImportHistory />;
  } else if (isSmartImportReview) {
    content = <ImportReview />;
  } else if (isRecord) {
    content = <RecordTransaction />;
  } else if (tab === 'budgets') {
    content = <BudgetsTab month={month} />;
  } else if (tab === 'reports') {
    content = <ReportsTab month={month} />;
  } else {
    content = <TransactionsLedger month={month} onOpenSmartImport={() => setSmartImportOpen(true)} />;
  }

  return (
    <FinanceShell activeTab={tab} month={month} onMonthChange={setMonth} showMonth>
      {content}
      <SmartImportDialog open={smartImportOpen} onClose={() => setSmartImportOpen(false)} />
    </FinanceShell>
  );
}
